import { formatDistance } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSocket } from '../../hooks/use-socket'
import { IconButton, Popover } from '@mui/material'
import { useContext, useEffect, useState, useRef, MouseEvent } from 'react'

import './styles/index.css'
import { CountrySelector } from './country-selector'
import MenuIcon from '../../assets/icons/hamburger-menu-icon.svg'
import { normalizeCustomerCompanyFundingData } from '../../utils'
import { getFundingsDataByCountry } from '../../api/app-queries.api'
import NotificationIcon from '../../assets/icons/notification-icon-white.svg'
import { AuthContext, UserContext, NotificationsContext } from '../../contexts'
import NotificationOnIcon from '../../assets/icons/notification-on-icon-white.svg'
import { handleDeleteDataByOne, handleGetDataFromTableById } from '../../utils/db/common-methods'
import { CustomerCompanyFunding, FundingNotification, SystemUser, UserSessionType } from '../../types'
import { RejectCustomerCompanyFundingModal } from '../customer-company-details-management/reject-customer-company-funding-modal'
import { RevertCustomerCompanyFundingModal } from '../customer-company-details-management/revert-customer-company-funding-modal'
import { CustomerCompanyFundingDetailsModal } from '../customer-company-details-management/customer-company-funding-details-modal'

const Header = ({ handleDrawerToggle }: { handleDrawerToggle: () => void }) => {
  const navigate = useNavigate()
  const { t } = useTranslation(['dashboard'])
  const { removeAuth } = useContext(AuthContext)
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { userSession, setUserSessionData } = useContext(UserContext)
  const { fundingsNotifications, updateFundingsNotificationsData, updateNewNotificationStatus } = useContext(NotificationsContext)

  const currentUserSessionRef = useRef<UserSessionType | null>(null)

  const [selectedCountry, setSelectedCountry] = useState('COL')
  const [notificationReadCount, setNotificationReadCount] = useState(0)
  const [newNotifications, setNewNotifications] = useState<boolean>(false)
  const [currentUserSession, setCurrentUserSession] = useState<Partial<SystemUser>>({})
  const [anchorFundingNotifications, setAnchorFundingNotifications] = useState<HTMLButtonElement | null>(null)
  const [selectedCustomerCompanyFunding, setSelectedCustomerCompanyFunding] = useState<CustomerCompanyFunding>()
  const [openRevertCustomerCompanyFundingModal, setOpenRevertCustomerCompanyFundingModal] = useState<boolean>(false)
  const [openRejectCustomerCompanyFundingModal, setOpenRejectCustomerCompanyFundingModal] = useState<boolean>(false)
  const [openCustomerCompanyFundingDetailsModal, setOpenCustomerCompanyFundingDetailsModal] = useState<boolean>(false)

  const openFundingNotifications = Boolean(anchorFundingNotifications)
  const id = openFundingNotifications ? 'funding-notifications-popover' : undefined

  useEffect(() => {
    if (userSession) {
      setCurrentUserSession(userSession)
      currentUserSessionRef.current = userSession
    }

    const currentSelectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
    setSelectedCountry(currentSelectedCountry)

    if (userSession?.belongsTo === 'holdings') {
      handleGetFundingsData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession])

  useEffect(() => {
    if (userSession && userSession?.belongsTo === 'holdings') {
      if (!appDataSocket) return

      appDataSocket.on('addNewCustomerCompanyFunding', async () => {
        const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
        handleGetFundingsDataFromRemote(selectedCountry)
      })

      appDataSocket.on('updateCustomerCompanyFunding', async () => {
        const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
        handleGetFundingsDataFromRemote(selectedCountry)
      })

      appDataSocket.on('deleteCustomerCompanyFunding', async (dataFromSocket: any) => {
        handleDeleteCustomerCompanyFundingFromLocalDB(dataFromSocket.documentKey)
        const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
        handleGetFundingsDataFromRemote(selectedCountry)
      })

      appDataSocket.on('updateSystemUser', async (dataFromSocket: any) => {
        if (String(dataFromSocket.systemUserId) === String(userSession._id)) {
          handleUpdateUserSession(dataFromSocket.systemUserDataUpdated)
        }
      })

      appDataSocket.on('deleteSystemUser', async (dataFromSocket: any) => {
        if (String(dataFromSocket.documentKey) === String(userSession._id)) {
          removeAuth?.()
          navigate('/', { replace: true, state: { sessionExpired: true } })
        }
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appDataSocket, userSession, isAppDataSocketInitialized])

  useEffect(() => {
    const thereAreNewFundingsNotifications = fundingsNotifications?.filter((fundingNotification) => fundingNotification.status === 'processing')

    if (Array.isArray(thereAreNewFundingsNotifications) && thereAreNewFundingsNotifications.length > 0) {
      setNewNotifications(true)
    } else {
      setNewNotifications(false)
    }
  }, [fundingsNotifications])

  useEffect(() => {
    let countInterval: any = null

    if (anchorFundingNotifications) {
      countInterval = setInterval(() => {
        setNotificationReadCount((prevValue) => prevValue + 1)
      }, 1000)
    } else {
      setNotificationReadCount(0)
    }

    return () => clearInterval(countInterval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorFundingNotifications])

  useEffect(() => {
    if (notificationReadCount >= 5) {
      const currentFundingsNotifications = fundingsNotifications as Array<FundingNotification>
      const fundingsNotificationsUpdated = currentFundingsNotifications.map((fundingNotification: FundingNotification) => {
        fundingNotification.read = true
        return fundingNotification
      })

      updateFundingsNotificationsData?.(fundingsNotificationsUpdated)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationReadCount])

  const handleUpdateUserSession = (userSessionUpdated: SystemUser) => {
    const createdAt = userSessionUpdated.createdAt ? new Date(userSessionUpdated.createdAt) : new Date()
    const systemUserUpdated = {
      subRole: '',
      role: userSessionUpdated.role,
      createdAt: createdAt.toISOString(),
      _id: String(userSessionUpdated._id),
      username: userSessionUpdated.username,
      lastName: userSessionUpdated.lastName,
      firstName: userSessionUpdated.firstName,
      activated: userSessionUpdated.activated,
      belongsTo: userSessionUpdated.belongsTo,
      adminOfPockets: userSessionUpdated.adminOfPockets
    }

    if ((userSessionUpdated as any).subRole) {
      systemUserUpdated.subRole = (userSessionUpdated as any).subRole
    }

    setUserSessionData?.(systemUserUpdated)
    setCurrentUserSession(systemUserUpdated)
    globalThis.sessionStorage.setItem('userSession', JSON.stringify(systemUserUpdated))
  }

  const handleGetFundingsData = async () => {
    let localFundingsDataToSet: any = {}
    const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
    const localFundingsData = globalThis.sessionStorage.getItem(`fundingsData:${selectedCountry}`)

    if (localFundingsData) {
      localFundingsDataToSet = JSON.parse(localFundingsData)
      handleSetInitialFundingsData(localFundingsDataToSet)
    }

    handleGetFundingsDataFromRemote(selectedCountry)
  }

  const handleGetFundingsDataFromRemote = async (selectedCountry: string) => {
    try {
      const { data } = await getFundingsDataByCountry(selectedCountry)

      globalThis.sessionStorage.setItem(`fundingsData:${selectedCountry}`, JSON.stringify(data))
      handleSetInitialFundingsData(data)
    } catch (error) {
      console.log('Error handleGetFundingsDataFromRemote > ', error)
    }
  }

  const handleSetInitialFundingsData = (dataToSet: any) => {
    const currentFundingsData = dataToSet.currentFundingsData
    const fundingsNotificationsUpdated = [] as Array<FundingNotification>

    for (const fundingData of currentFundingsData) {
      const fundingDataNormalizedData = normalizeCustomerCompanyFundingData(fundingData) as CustomerCompanyFunding
      const fundingNotificationAlreadyAdded = fundingsNotificationsUpdated.find((fundingNotification) => String(fundingNotification._id) === String(fundingDataNormalizedData._id))

      if (fundingDataNormalizedData.status === 'processing' && !fundingNotificationAlreadyAdded) {
        fundingsNotificationsUpdated.push({
          read: false,
          status: fundingDataNormalizedData.status,
          _id: String(fundingDataNormalizedData._id),
          createdAt: fundingDataNormalizedData.createdAt,
          customerCompanyFundingData: fundingDataNormalizedData,
          text: t('notifications.fundingApprove', { customerCompanyName: fundingDataNormalizedData.assignedTo.customerCompanyBusinessName.toUpperCase() })
        })

        setNewNotifications(true)
      }

      if (fundingNotificationAlreadyAdded && (fundingDataNormalizedData.status === 'approved' || fundingDataNormalizedData.status === 'rejected')) {
        const fundingNotificationIndex = fundingsNotificationsUpdated.findIndex((fundingNotification) => String(fundingNotification._id) === String(fundingDataNormalizedData._id))
        fundingsNotificationsUpdated.splice(fundingNotificationIndex, 1)
      }

      if (
        fundingDataNormalizedData.status === 'rejected' &&
        selectedCustomerCompanyFunding &&
        String(selectedCustomerCompanyFunding._id) === String(fundingDataNormalizedData._id) &&
        String(userSession?._id) !== String(fundingDataNormalizedData.rejectedBy.systemUserId)
      ) {
        setOpenRevertCustomerCompanyFundingModal(false)
        setOpenRejectCustomerCompanyFundingModal(false)
        setOpenCustomerCompanyFundingDetailsModal(false)
        updateNewNotificationStatus?.(true, 'someone-else-resolve-allocation')
      }

      if (
        fundingDataNormalizedData.status === 'approved' &&
        selectedCustomerCompanyFunding &&
        String(selectedCustomerCompanyFunding._id) === String(fundingDataNormalizedData._id) &&
        String(userSession?._id) !== String(fundingDataNormalizedData.assignedBy.systemUserId)
      ) {
        setOpenRevertCustomerCompanyFundingModal(false)
        setOpenRejectCustomerCompanyFundingModal(false)
        setOpenCustomerCompanyFundingDetailsModal(false)
        updateNewNotificationStatus?.(true, 'someone-else-resolve-allocation')
      }
    }

    fundingsNotificationsUpdated.sort((thisFundingNotification: FundingNotification, nextFundingNotification: FundingNotification) => {
      return Date.parse(String(nextFundingNotification.createdAt)) - Date.parse(String(thisFundingNotification.createdAt))
    })

    const thereAreNewFundingsNotifications = fundingsNotificationsUpdated?.filter((fundingNotification) => fundingNotification.status === 'processing')

    if (Array.isArray(thereAreNewFundingsNotifications) && thereAreNewFundingsNotifications.length > 0) {
      setNewNotifications(true)
      setNotificationReadCount(0)
    }

    updateFundingsNotificationsData?.(fundingsNotificationsUpdated)
  }

  const handleDeleteCustomerCompanyFundingFromLocalDB = async (documentKey: string) => {
    const customerCompanyFundingDataSetted: Array<CustomerCompanyFunding> = (await handleGetDataFromTableById('customer-company-fundings', String(documentKey))) as Array<CustomerCompanyFunding>

    if (customerCompanyFundingDataSetted.length > 0) {
      await handleDeleteDataByOne('customer-company-fundings', documentKey)
    }
  }

  return (
    <>
      <header className="header-container">
        <div className="header-menu-button">
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ m: 0, color: '#FFF', display: { sm: 'none' } }}
          >
            <img
              src={MenuIcon}
              alt="menu-icon"
              style={{ width: '24px' }}
            />
          </IconButton>
          <div className="header-menu-button-text">{t('appBarTexts.hamburguerMenu')}</div>
        </div>
        <div className="header-notifications-container">
          <CountrySelector
            selectedCountry={selectedCountry}
            setSelectedCountry={setSelectedCountry}
          />
          <div className="header-greeting">{t('appBarTexts.greeting', { firstName: currentUserSession?.firstName })}</div>
          {userSession?.subRole && (userSession?.subRole === 'payments-specialist' || userSession?.subRole === 'crypto-specialist') && (
            <>
              {newNotifications ? (
                <div
                  className="notifications-button-container"
                  onClick={(event: MouseEvent<HTMLDivElement>) => setAnchorFundingNotifications(event.currentTarget as unknown as HTMLButtonElement | null)}
                >
                  <img
                    src={NotificationOnIcon}
                    alt="notification-on-icon"
                  />
                </div>
              ) : (
                <div className="notifications-button-container">
                  <img
                    src={NotificationIcon}
                    alt="notification-icon"
                  />
                </div>
              )}
            </>
          )}
        </div>
        <Popover
          id={id}
          open={openFundingNotifications}
          anchorEl={anchorFundingNotifications}
          onClose={(event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation()
            setAnchorFundingNotifications(null)
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          {fundingsNotifications?.map((fundingNotification: FundingNotification, index: number) => (
            <div
              key={index}
              className="notification-container"
              onClick={() => {
                setSelectedCustomerCompanyFunding(fundingNotification.customerCompanyFundingData)
                setOpenCustomerCompanyFundingDetailsModal(true)
              }}
            >
              <div className={fundingNotification.read ? 'notification-read-text' : 'notification-unread-text'}>{fundingNotification.text}</div>
              <div className={fundingNotification.read ? 'notification-read-time' : 'notification-unread-time'}>{formatDistance(new Date(), new Date(fundingNotification.createdAt), { includeSeconds: true })}</div>
            </div>
          ))}
        </Popover>
      </header>
      {openCustomerCompanyFundingDetailsModal && (
        <CustomerCompanyFundingDetailsModal
          selectedCustomerCompanyFunding={selectedCustomerCompanyFunding}
          handleOpenRevertCustomerCompanyFundingModal={() => setOpenRevertCustomerCompanyFundingModal(true)}
          handleOpenRejectCustomerCompanyFundingModal={() => setOpenRejectCustomerCompanyFundingModal(true)}
          handleSetModalOpenState={(openState: boolean) => setOpenCustomerCompanyFundingDetailsModal(openState)}
        />
      )}
      {openRejectCustomerCompanyFundingModal && (
        <RejectCustomerCompanyFundingModal
          selectedCustomerCompanyFunding={selectedCustomerCompanyFunding}
          handleCloseFundingAllocationModal={() => setOpenCustomerCompanyFundingDetailsModal(false)}
          handleSetModalOpenState={(openState: boolean) => setOpenRejectCustomerCompanyFundingModal(openState)}
        />
      )}
      {openRevertCustomerCompanyFundingModal && (
        <RevertCustomerCompanyFundingModal
          selectedCustomerCompanyFunding={selectedCustomerCompanyFunding}
          handleCloseFundingAllocationModal={() => setOpenCustomerCompanyFundingDetailsModal(false)}
          handleSetModalOpenState={(openState: boolean) => setOpenRevertCustomerCompanyFundingModal(openState)}
        />
      )}
    </>
  )
}

export default Header
